import { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Flex,
    Spinner,
    Text
} from '@chakra-ui/react'
import { useSearchParams } from 'react-router-dom'
import { useTeacherRegistry } from '@/shared/api/hooks/useTeacherRegistry'
import { inTeacherScope, useTeacherScope } from '@/shared/api/hooks/useTeacherScope'
import { RegistryTeacher, TeacherStatus, normalizeStatus } from '../types'
import TeacherList, { StatusFilter } from './TeacherList'
import TeacherDetails from './TeacherDetails'

const DEFAULT_PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 350

const isStatusFilter = (value: string | null): value is StatusFilter =>
    value === 'all' || value === 'pending' || value === 'active' || value === 'suspended'

const matchesSearch = (teacher: RegistryTeacher, term: string) => {
    if (!term) return true
    const haystack = [
        teacher.firstName,
        teacher.lastName,
        teacher.email,
        teacher.phoneNumber,
        teacher.branch?.name
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    return haystack.includes(term)
}

export default function TeacherRegistryView() {
    // The registry is small enough to fetch whole (limit 1000) and the list
    // endpoint documents no search param, so filtering/paging happen client-side.
    const { teachers: allTeachers, loading, fetching, error } = useTeacherRegistry()
    // A branch admin manages their own branch; a global admin manages the
    // church. Same rule the notification feed applies, from the same hook —
    // when only one of the two scoped, the sidebar badge and the count at the
    // top of this page reported different sizes for the same backlog.
    const { scopeBranchId, ready: scopeReady } = useTeacherScope()

    const teachers = useMemo(
        () =>
            scopeBranchId
                ? allTeachers.filter((teacher) => inTeacherScope(teacher, scopeBranchId))
                : allTeachers,
        [allTeachers, scopeBranchId]
    )

    // What the scope is holding back, so a branch admin knows the rest of the
    // church's backlog exists and whose job it is — rather than wondering why
    // a request someone mentioned isn't here.
    const outOfScopePending = useMemo(() => {
        if (!scopeBranchId) return 0
        return allTeachers.filter(
            (teacher) =>
                normalizeStatus(teacher.status) === 'pending' &&
                !inTeacherScope(teacher, scopeBranchId)
        ).length
    }, [allTeachers, scopeBranchId])

    // `?status=pending` deep-links here from the notification feed and from the
    // retired Pending Teachers route.
    const [searchParams, setSearchParams] = useSearchParams()
    const statusParam = searchParams.get('status')
    const status: StatusFilter = isStatusFilter(statusParam) ? statusParam : 'all'
    // `?teacherId=` targets one specific record, so arriving from a
    // notification selects that teacher rather than whichever row sorts first.
    const requestedId = searchParams.get('teacherId')

    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    // Store only the id (not the whole record) so the selection can't point at
    // stale data once the underlying list is refetched.
    const [selectedId, setSelectedId] = useState<string | null>(null)

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim().toLowerCase())
            setPage(1)
            setSelectedId(null)
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchInput])

    const setStatus = (next: StatusFilter) => {
        setSearchParams(next === 'all' ? {} : { status: next }, { replace: true })
        setPage(1)
        setSelectedId(null)
    }

    const counts = useMemo(() => {
        const tally: Record<TeacherStatus, number> = {
            pending: 0,
            active: 0,
            suspended: 0
        }
        for (const teacher of teachers) tally[normalizeStatus(teacher.status)] += 1
        return tally
    }, [teachers])

    const filtered = useMemo(
        () =>
            teachers
                .filter(
                    (teacher) =>
                        status === 'all' || normalizeStatus(teacher.status) === status
                )
                .filter((teacher) => matchesSearch(teacher, search)),
        [teachers, status, search]
    )

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize))

    const requestedIndex = requestedId
        ? filtered.findIndex((teacher) => teacher._id === requestedId)
        : -1
    // Requested but absent — already approved or rejected by someone else, or
    // filtered out. Quietly selecting row 0 here is exactly how the wrong
    // teacher gets approved, so select nobody and say why.
    const requestedMissing = !!requestedId && requestedIndex < 0 && !loading

    // Page is derived, not synced: while `?teacherId=` is present it resolves to
    // whichever page holds that teacher. Any interaction below clears the param,
    // handing control back to `page`.
    const requestedPage =
        requestedIndex >= 0 ? Math.floor(requestedIndex / pageSize) + 1 : null
    const currentPage = Math.min(requestedPage ?? page, pageCount)
    const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

    /** Release the deep link so the teacher's own navigation takes over. */
    const clearRequested = () => {
        if (!requestedId) return
        const next = new URLSearchParams(searchParams)
        next.delete('teacherId')
        setSearchParams(next, { replace: true })
    }

    // While a specific teacher is targeted, resolve against the whole filtered
    // set rather than the visible page, so the first row never flashes as
    // selected before the page resolves.
    const selected = requestedId
        ? filtered.find((teacher) => teacher._id === requestedId) ?? null
        : // Keep the selection sticky within the page; fall back to the first row.
          visible.find((teacher) => teacher._id === selectedId) ?? visible[0] ?? null

    // One sentence, built here rather than assembled in the markup: split
    // across elements it reads the same but cannot be found as a whole.
    const scopeNote =
        `Showing your branch. ${outOfScopePending} other ` +
        `${outOfScopePending === 1 ? 'request is' : 'requests are'} awaiting ` +
        'approval elsewhere in the church — those belong to that branch’s ' +
        'admin, or to a global admin.'

    if (error) {
        return (
            <Box>
                <Text color="red.500">Error: {error.message}</Text>
            </Box>
        )
    }

    // Nothing is listed until the scope is known. Gating only on `loading`
    // was not enough: the rows arrive first, and an unresolved branch reads
    // as "no scope", so the whole church flashed up before being filtered.
    if (!scopeReady || (loading && teachers.length === 0)) {
        return (
            <Flex justify="center" align="center" height="400px">
                <Spinner size="xl" />
            </Flex>
        )
    }

    return (
        <>
            {outOfScopePending > 0 ? (
                <Alert status="info" borderRadius="md" mb={3} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>{scopeNote}</AlertDescription>
                </Alert>
            ) : null}
            {requestedMissing ? (
                <Alert status="warning" borderRadius="md" mb={3} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                        That request is no longer in this list — it may have already been
                        approved or rejected. Nothing has been selected.
                    </AlertDescription>
                </Alert>
            ) : null}
            <Flex
                gap={4}
                align="stretch"
                direction={{ base: 'column', md: 'row' }}
                flex="1"
                minH="0"
            >
                <Box
                    w={{ base: 'full', md: '320px' }}
                    flexShrink={0}
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="md"
                    overflow="hidden"
                >
                    <TeacherList
                        teachers={visible}
                        total={filtered.length}
                        counts={counts}
                        allCount={teachers.length}
                        status={status}
                        onStatusChange={setStatus}
                        search={searchInput}
                        onSearch={(value) => {
                            clearRequested()
                            setSearchInput(value)
                        }}
                        selectedId={selected?._id ?? null}
                        onSelect={(id) => {
                            clearRequested()
                            setSelectedId(id)
                        }}
                        page={currentPage}
                        pageCount={pageCount}
                        onPageChange={(nextPage) => {
                            clearRequested()
                            setPage(nextPage)
                            setSelectedId(null)
                        }}
                        pageSize={pageSize}
                        onPageSizeChange={(size) => {
                            clearRequested()
                            setPageSize(size)
                            setPage(1)
                            setSelectedId(null)
                        }}
                        loading={fetching}
                    />
                </Box>
                <Box
                    flex="1"
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="md"
                    overflowY="auto"
                >
                    <TeacherDetails teacher={selected} />
                </Box>
            </Flex>
        </>
    )
}
